import { FC } from "react";
import { WorkDTO } from "../../../domain/models/dto/WorkDTO";
import { getWorkUserProfileImage, isCurrentWorkOwner, isExpressWork } from "../../../slices/works-slice";
import RatingStarSVG from "../svg/RatingStarIconSVG";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectActiveSubscription } from "../../../slices/subscriptions-slice";
import LockIconSVG from "../svg/LockIconSVG";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
    getTelegramContactLink,
    getViberContactLink,
    getWhatsAppContactLink,
} from "../../../tools/ContactLinkTools";

interface UserContactsCardProps {
    work: WorkDTO;
}

const UserContactsCard: FC<UserContactsCardProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const subscription = useSelector(selectActiveSubscription);
    const isOwner = useSelector(isCurrentWorkOwner(props.work));
    const isExpress = isExpressWork(props.work);

    const isContactsBlockVisible = subscription || isOwner || !isExpress;
    const telegramContactLink = getTelegramContactLink(props.work?.telegram);
    const isContactsInfoVisible = props.work?.phones?.length || telegramContactLink;

    return (
        <div
            onClick={() => navigate(`/profile/${props.work.clientId}`)}
            className="cursor-pointer relative flex flex-col items-start w-[519px] dmax2:w-[360px] h-fit bg-backgroundBlock border border-backgroundButton rounded-2xl dmax2:rounded-lg px-9 dmax2:px-5 py-6 dmax2:py-4 tmax5:mt-10 tmax5:mb-10"
        >
            <p className="H24B dmax2:H20B text-textWhite mb-6 dmax2:mb-4">{t("work.orderer")}</p>
            <div className="relative flex items-center w-full">
                <div className="relative min-w-[70px] max-w-[70px] dmax2:min-w-[56px] dmax2:max-w-[56px] h-[70px] min-h-[70px] max-h-[70px] dmax2:h-[56px] dmax2:min-h-[56px] dmax2:max-h-[56px] mr-4 dmax2:mr-3">
                    <img
                        src={getWorkUserProfileImage(props.work.client, true)}
                        alt="user-photo"
                        className="rounded-full cursor-pointer object-cover w-full h-full"
                    />
                </div>
                <p className="H20B dmax2:H16B text-textWhite line-clamp-2">{props.work.client?.firstName}</p>
                <div className="flex items-center ml-4 dmax2:ml-2">
                    <span className="H16 text-textWhite mr-[2px]">{props.work.client?.avgRating}</span>
                    <RatingStarSVG className="w-5 mr-1" />
                    <span className="H16 text-textFaded">{props.work.client?.reviewsCount}</span>
                </div>
            </div>
            {isContactsBlockVisible ? (
                isContactsInfoVisible && (
                    <div className="mt-6">
                        <p className="H20B text-textWhite mr-6 mt-2 mmax2:mb-2">{t("common.contacts")}</p>
                        <div className="flex flex-col justify-center">
                            <div className="flex flex-col items-start">
                                {props.work?.phones?.map((phone, index) => (
                                    <div
                                        key={`user-phone-${index}`}
                                        className="flex items-center justify-center mmax2:justify-start my-2 tmax4:my-[6px]"
                                    >
                                        <span className="H20B dmax2:H16B text-textWhite mr-5">{phone.phone}</span>
                                        <div className="flex items-center">
                                            {phone.viber && getViberContactLink(phone.phone) && (
                                                <a
                                                    href={getViberContactLink(phone.phone)!}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <img
                                                        src="/images/viber.png"
                                                        alt="viber"
                                                        className="w-9 dmax2:w-8 mr-4"
                                                    />
                                                </a>
                                            )}
                                            {phone.whatsapp && getWhatsAppContactLink(phone.phone) && (
                                                <a
                                                    href={getWhatsAppContactLink(phone.phone)!}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <img
                                                        src="/images/whatsapp.png"
                                                        alt="whatsapp"
                                                        className="w-9 dmax2:w-8"
                                                    />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {telegramContactLink && (
                                <a
                                    href={telegramContactLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    className={`flex items-center ${
                                        props.work?.phones?.length === 0 ? "mt-[6px]" : "mt-2"
                                    }`}
                                >
                                    <span className="H20B dmax2:H16B text-textWhite mr-3 dmax2:mr-2">
                                        {t("userProfile.fields.telegramField")}
                                    </span>
                                    <img src="/images/telegram.png" alt="telegram" className="w-9 dmax2:w-8 mr-4" />
                                </a>
                            )}
                        </div>
                    </div>
                )
            ) : (
                <div className="flex flex-col items-center justify-center bg-[rgba(54,54,57,1)] border border-[rgba(45,45,50,1)] rounded-[8px] w-full h-[245px] dmax2:h-[196px] px-5 mt-6 dmax2:mt-4">
                    <LockIconSVG className="text-textFaded" />
                    <span className="H20B dmax2:H16B text-textWhite text-center max-w-[470px] mt-5 dmax2:mt-3">
                        {t("work.ordererClosed")}
                    </span>
                    <Link
                        to="/subscription"
                        className="button H16B dmax2:H14B text-textWhite h-[50px] dmax2:h-10 px-6 dmax2:px-4 cursor-pointer transition-all duration-300 mt-5 dmax2:mt-3"
                    >
                        {t("main.callToSubscribeButton")}
                    </Link>
                </div>
            )}
        </div>
    );
};

export default UserContactsCard;
